import { useEffect, useState } from 'react'
import { NavLink, useLocation } from 'react-router'
import { ChevronDown, ChevronRight, Menu, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useVisibleNav } from './useVisibleNav'
import { isPathUnder } from './nav-visibility'
import { loadNavOpenState, saveNavOpenState, type NavOpenState } from './nav-open-state'
import type { V2NavItem } from './nav-config'
import { InquiryNavBadge } from './components/InquiryNavBadge'

const itemCls = 'flex items-center gap-2 border-l-4 px-4 py-1.5 text-[0.8125rem]'
const activeCls = 'border-[#0972d3] bg-[#f1faff] font-bold text-[#0972d3]'
const idleCls = 'border-transparent text-[#414d5c] hover:text-[#0972d3]'

/** 실제 페이지로 가는 항목. nested 면 2단계 자식(들여쓰기, 아이콘 없음) */
function NavLeaf({ item, nested = false }: { item: V2NavItem; nested?: boolean }) {
  return (
    <li>
      <NavLink
        to={item.to}
        end={item.to === '/v2' || item.exact}
        className={({ isActive }) => cn(itemCls, nested && 'pl-10 text-xs', isActive ? activeCls : idleCls)}
      >
        {item.icon && <item.icon className="size-3.5" />}
        {item.label}
        {item.badge === 'inquiryNeedsReply' && <InquiryNavBadge />}
      </NavLink>
    </li>
  )
}

/** 2단계 부모 — 링크가 아닌 토글 버튼. 활성(하위 경로에 있음)이면 글자만 강조, 왼쪽 바는 실제 페이지인 자식에만 */
function NavParent({ item, open, active, onToggle }: { item: V2NavItem; open: boolean; active: boolean; onToggle: () => void }) {
  const Chevron = open ? ChevronDown : ChevronRight
  return (
    <li>
      <button
        type="button"
        aria-expanded={open}
        onClick={onToggle}
        className={cn(itemCls, 'w-full border-transparent', active ? 'font-bold text-[#0972d3]' : 'text-[#414d5c] hover:text-[#0972d3]')}
      >
        {item.icon && <item.icon className="size-3.5" />}
        {item.label}
        <Chevron className="ml-auto size-3.5" />
      </button>
      {open && (
        <ul>
          {item.children?.map((child) => (
            <NavLeaf key={child.to} item={child} nested />
          ))}
        </ul>
      )}
    </li>
  )
}

/**
 * AWS Console풍 사이드 네비 — 밝은 배경(v1 다크 사이드바와 반대), 접기 지원. 항목은 권한 필터 적용(FIKET-417).
 * 2단계 메뉴(FIKET-428): 하위 경로에 들어오면 자동 펼침, 사용자가 토글한 상태는 localStorage 에 기억.
 */
export function SideNav() {
  const [collapsed, setCollapsed] = useState(false)
  const { groups } = useVisibleNav()
  const { pathname } = useLocation()
  const [open, setOpen] = useState<NavOpenState>(loadNavOpenState)

  // 하위 경로 진입 시 해당 부모 자동 펼침 (수동으로 접어둔 것도 다시 들어오면 펼친다)
  useEffect(() => {
    const activeParents = groups
      .flatMap((g) => g.items)
      .filter((item) => item.children && isPathUnder(pathname, item.to))
    if (activeParents.length === 0) return
    setOpen((prev) => {
      if (activeParents.every((p) => prev[p.to])) return prev
      const next = { ...prev }
      for (const p of activeParents) next[p.to] = true
      return next
    })
  }, [pathname, groups])

  useEffect(() => {
    saveNavOpenState(open)
  }, [open])

  const toggle = (to: string) => setOpen((prev) => ({ ...prev, [to]: !prev[to] }))

  if (collapsed) {
    return (
      <aside className="sticky top-10 h-[calc(100vh-2.5rem)] w-10 shrink-0 border-r border-[#d5dbdb] bg-white">
        <button
          type="button"
          onClick={() => setCollapsed(false)}
          aria-label="메뉴 펼치기"
          className="flex h-10 w-full items-center justify-center text-[#414d5c] hover:text-[#0972d3]"
        >
          <Menu className="size-4" />
        </button>
      </aside>
    )
  }

  return (
    <aside className="sticky top-10 flex h-[calc(100vh-2.5rem)] w-60 shrink-0 flex-col overflow-y-auto border-r border-[#d5dbdb] bg-white">
      <div className="flex items-center justify-between border-b border-[#d5dbdb] px-4 py-2.5">
        <span className="text-sm font-bold text-[#16191f]">Fiket Admin</span>
        <button
          type="button"
          onClick={() => setCollapsed(true)}
          aria-label="메뉴 접기"
          className="text-[#414d5c] hover:text-[#0972d3]"
        >
          <X className="size-4" />
        </button>
      </div>

      <nav className="flex-1 py-2">
        {groups.map((group, i) => (
          <div key={group.label ?? i} className="mb-1">
            {group.label && (
              <div className="px-4 pt-3 pb-1 text-xs font-bold text-[#16191f]">{group.label}</div>
            )}
            <ul>
              {group.items.map((item) =>
                item.children ? (
                  <NavParent
                    key={item.to}
                    item={item}
                    open={!!open[item.to]}
                    active={isPathUnder(pathname, item.to)}
                    onToggle={() => toggle(item.to)}
                  />
                ) : (
                  <NavLeaf key={item.to} item={item} />
                ),
              )}
            </ul>
          </div>
        ))}
      </nav>
    </aside>
  )
}
